/**
 * Authentication Context
 * Provides authentication state and methods throughout the app
 * Uses httpOnly cookies for secure authentication
 * Implements lazy auth: only validates on protected actions, not on every page load
 */

import { useLogin, useLogout, useProfile, useRegister } from '@shared/lib/api'
import { analytics, EVENTS } from '@shared/lib/analytics'
import { credentialsStorage, userStorage, tokenStorage } from '@shared/lib/storage'
import { logger } from '@shared/lib/utils/logger'
import type { LoginCredentials, RegisterData, User } from '@shared/types'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'

// ============================================================================
// Types
// ============================================================================

interface AuthContextValue {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  authChecked: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  register: (data: RegisterData, identifierType: 'phone' | 'email') => Promise<void>
  logout: () => Promise<void>
  refetchUser: () => Promise<User | null>
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

// ============================================================================
// Provider
// ============================================================================

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Initialize from localStorage (lazy auth - no initial backend call)
  // Check if we have tokens OR user data
  const [isAuthenticated, setIsAuthenticated] = useState(
    userStorage.hasUser() || tokenStorage.hasToken()
  )
  const [localUser, setLocalUser] = useState<User | null>(userStorage.getUser())
  const [authChecked, _setAuthChecked] = useState(true) // No need to check initially - loaded from storage

  // Identify users restored from storage on page reload so PostHog links events to a person.
  // Empty deps is intentional: localUser is synchronously hydrated from localStorage in useState
  // above, so its mount-time value is the restored user. If hydration ever becomes async this
  // effect must be revisited.
  useEffect(() => {
    if (localUser) analytics.identify(localUser.id)
  }, [])

  // Fetch user profile (disabled by default - only on demand)
  const {
    data: _serverUser,
    isLoading,
    refetch: refetchUserQuery,
  } = useProfile(false) // Changed from true to false - lazy load only

  // Mutations
  const loginMutation = useLogin()
  const registerMutation = useRegister()
  const logoutMutation = useLogout()

  // Login handler
  const login = async (credentials: LoginCredentials) => {
    try {
      await loginMutation.mutateAsync(credentials)
      // Refetch to get fresh user data
      const result = await refetchUserQuery()

      if (result.data) {
        // Only save to localStorage if remember is true
        if (credentials.remember) {
          userStorage.setUser(result.data)
        }
        setLocalUser(result.data)
        setIsAuthenticated(true)
        analytics.identify(result.data.id)
      } else {
        throw new Error('Failed to fetch user profile after login')
      }
    } catch (error) {
      setIsAuthenticated(false)
      throw error
    }
  }

  // Register handler
  const register = async (data: RegisterData, identifierType: 'phone' | 'email') => {
    try {
      await registerMutation.mutateAsync(data)
      // Capture immediately — account exists at this point regardless of whether
      // the subsequent profile refetch succeeds
      analytics.capture(EVENTS.ACCOUNT_CREATED, { identifier_type: identifierType })
      const result = await refetchUserQuery()

      if (result.data) {
        // Save to localStorage
        userStorage.setUser(result.data)
        setLocalUser(result.data)
        setIsAuthenticated(true)
        analytics.identify(result.data.id)
      } else {
        throw new Error('Failed to fetch user profile after registration')
      }
    } catch (error) {
      setIsAuthenticated(false)
      throw error
    }
  }

  // Logout handler
  const logout = async () => {
    try {
      await logoutMutation.mutateAsync()

      // Clear user session and tokens from localStorage
      // NOTE: credentials are intentionally NOT cleared - they persist for "Remember Me" functionality
      userStorage.clearUser()
      tokenStorage.clearToken()

      setLocalUser(null)
      setIsAuthenticated(false)
      analytics.reset()

      // Verify clearing worked
      credentialsStorage.verifyLogout()
    } catch (error) {
      logger.error('[AuthContext] Logout error', error)
      // Even if logout fails, clear local state
      // NOTE: credentials are NOT cleared on error either - preserve Remember Me
      userStorage.clearUser()
      tokenStorage.clearToken()
      setLocalUser(null)
      setIsAuthenticated(false)
      throw error
    }
  }

  /**
   * Sync local user with server (call before protected actions)
   * Memoized to prevent unnecessary re-renders in consuming components
   */
  const syncUser = useCallback(async (): Promise<User | null> => {
    try {
      const result = await refetchUserQuery()

      if (result.data) {
        userStorage.setUser(result.data)
        setLocalUser(result.data)
        setIsAuthenticated(true)
        return result.data
      } else {
        // Auth failed - clear local data
        userStorage.clearUser()
        setLocalUser(null)
        setIsAuthenticated(false)
        return null
      }
    } catch (_error) {
      // Auth failed - clear local data
      userStorage.clearUser()
      setLocalUser(null)
      setIsAuthenticated(false)
      return null
    }
  }, [refetchUserQuery])

  const value: AuthContextValue = {
    user: localUser,
    isAuthenticated,
    isLoading,
    authChecked,
    login,
    register,
    logout,
    refetchUser: syncUser, // Use sync function instead of raw refetch
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ============================================================================
// Hook
// ============================================================================

export function useAuth() {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
